import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { SelectComponent, SmartSearchComponent, TableCellDirective, TableGridComponent, TableHeader } from 'mbs-ui-kit';
import { SmartSearchValidationState } from 'mbs-ui-kit/smart-search/models';
import { SmartSearchModel, getSmartSearchTagValues } from 'mbs-ui-kit/smart-search/models/smart-search-model';
import { Company, tableSelectAllEntitiesText, tableSelectedEntitiesText } from 'mbs-ui-shared';
import { CompanySelectComponent } from 'mbs-ui-shared@components/paged-select/company-select.component';
import { OSTypeModel, getOSTypes } from 'mbs-ui-shared@components/select-computers-modal/select-computers-modal.constants';
import { ComputerTagsFacade } from 'mbs-ui-shared@facades/computer.tags.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import Computer, { AgentType, GetComputersParams, OsType } from 'mbs-ui-shared@models/Computer';
import { Tag } from 'mbs-ui-shared@models/tag';
import { SmartSearchTemplateName } from 'mbs-ui-shared@modules/smart-search-templates/models/smart-search-templates-models';
import { SmartSearchTemplatesModule } from 'mbs-ui-shared@modules/smart-search-templates/smart-search-templates.module';
import { BehaviorSubject, Observable, debounceTime, filter, forkJoin, map, switchMap, tap, withLatestFrom } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'mbs-select-computer-utility',
  templateUrl: './select-computer-utility.component.html',
  styleUrls: ['./select-computer-utility.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SmartSearchTemplatesModule,
    CompanySelectComponent,
    I18NextPipe,
    SmartSearchComponent,
    TableGridComponent,
    TableCellDirective,
    TableGridComponent,
    SelectComponent,
    TableCellDirective
  ]
})
export class SelectComputerUtilityComponent implements OnInit, OnChanges, AfterViewInit {
  private computersFacade = inject(ComputersFacade);
  private tagFacade = inject(ComputerTagsFacade);
  private i18next = inject(I18NextPipe);

  @ViewChild(SmartSearchComponent, { static: false }) smartSearch: SmartSearchComponent;
  @ViewChild(TableGridComponent, { static: false }) tableGrid: TableGridComponent;
  @Input() selectedIds: string[] = [];
  @Input() showOffline = false;
  @Input() allFilters = false;
  @Input() multipleSelect = false;
  @Input() osType: OsType = null;
  @Input() agentType: AgentType = null;
  @Input() noDataText: string = null;

  // specific case
  @Input() isSpecificCase = false;
  @Input() optionalOrderingParams = null;
  @Input() selectedValue = null;
  //

  @Output() emitChanges = new EventEmitter<Computer[]>();

  public loading = true;
  public headers: TableHeader[] = [
    {
      name: 'Name',
      overflow: true,
      class: 'pl-0',
      gridColSize: '10fr'
    },
    {
      name: 'Company',
      overflow: true,
      class: 'pl-0',
      gridColSize: '10fr'
    }
  ];

  public computerSearchForm = new FormGroup<{ companies: FormControl<Company[]>; os: FormControl<OSTypeModel[]> }>({
    companies: new FormControl([]),
    os: new FormControl([])
  });

  public osTypes = getOSTypes(this.i18next);

  public infiniteScrollDistance = 0.5;
  public infiniteScrollThrottle = 150;
  public infiniteScrollLoading = false;
  public orderBy = 'name:asc';
  public total = 0;

  public data: Computer[] = [];
  public selected: Computer[] = [];

  public onScrolled$ = new BehaviorSubject<boolean>(false);

  private currentParams: GetComputersParams = null;
  private limit = 100;
  private offset = 0;

  loadSelected() {
    if (!this.selectedIds.length) return;

    this.loading = true;
    this.computersFacade.loadComputersByHids({ hids: this.selectedIds, includes: ['company'] });

    this.computersFacade.computerAll$.pipe(untilDestroyed(this)).subscribe({
      next: (data) => {
        if (!this.selectedIds.length) return;

        this.selected = data.filter((item) => this.selectedIds.includes(item.hid));
        this.data = this.getNewDataAndKeepSelected(this.data.filter((item) => this.selectedIds.includes(item.hid)));

        if (this.selectedIds.length === this.selected.length) this.selectedIds = [];
        this.loading = false;
      },
      error: () => {
        this.selected = [];
        this.loading = false;
      }
    });
  }

  getNewDataAndKeepSelected(data: Computer[]) {
    const selectedToBeAdded = this.selected.filter((item) => data.every((dataItem) => dataItem.hid !== item.hid));
    return selectedToBeAdded.concat(data);
  }

  getLoadStreamWithParams(
    params: GetComputersParams = { offset: this.offset, limit: this.limit, orderBy: this.orderBy },
    isInfiniteScroll = false
  ) {
    params = { ...params, includes: ['company'], ...this.getAdditionalParams() };
    if (this.isSpecificCase) {
      params = { ...params, ...this.optionalOrderingParams };
    }

    return this.computersFacade.getComputers(params).pipe(
      tap((response) => {
        this.currentParams = { ...params };
        this.total = response.total;
        const previousLength = this.data.length;

        if (params.offset > 0) {
          const newData = response.data.filter((item) => this.data.every((dataItem) => dataItem.hid !== item.hid));
          this.data = this.data.concat(newData);
          this.specificUseCaseAction(newData);
        } else {
          this.data = this.getNewDataAndKeepSelected(response.data);
          this.specificUseCaseAction(this.data);
        }

        if (isInfiniteScroll && previousLength === this.data.length) this.onScrolled$.next(true);
      })
    );
  }

  specificUseCaseAction(data: Computer[]) {
    if (this.isSpecificCase) {
      this.selected = this.selected.concat(data.filter((computer) => computer[this.selectedValue.name] === this.selectedValue.value));
    }
  }

  ngOnInit(): void {
    this.getLoadStreamWithParams().subscribe({
      next: () => (this.loading = false),
      error: () => {
        this.data = [];
        this.loading = false;
      }
    });

    this.onScrolled$
      .pipe(
        filter((value) => value && this.data.length < this.total),
        tap(() => (this.infiniteScrollLoading = true)),
        switchMap(() =>
          this.getLoadStreamWithParams(
            { ...this.currentParams, orderBy: this.orderBy, offset: this.currentParams.offset + this.limit },
            true
          )
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => (this.infiniteScrollLoading = false),
        error: () => {
          this.infiniteScrollLoading = false;
          this.data = [];
        }
      });

    setTimeout(() => {
      this.searchFormSubscriptionSetup();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedIds) {
      this.loadSelected();
    }
  }

  searchFormSubscriptionSetup() {
    this.computerSearchForm
      .get('companies')
      .valueChanges.pipe(
        debounceTime(300),
        switchMap((companies) => {
          if (companies) {
            this.currentParams.companyIds = companies.map((company) => company.id);
          } else {
            if (this.currentParams?.companyIds) delete this.currentParams.companyIds;
          }

          this.loading = true;

          return this.getLoadStreamWithParams({ ...this.currentParams, offset: 0, limit: this.limit, orderBy: this.orderBy });
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => (this.loading = false),
        error: () => {
          this.loading = false;
          this.data = [];
        }
      });

    this.computerSearchForm
      .get('os')
      .valueChanges.pipe(
        switchMap((selectedOS) =>
          this.getLoadStreamWithParams({
            ...this.currentParams,
            os: selectedOS.map((os) => os.id),
            offset: 0,
            limit: this.limit,
            orderBy: this.orderBy
          })
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => (this.loading = false),
        error: () => {
          this.loading = false;
          this.data = [];
        }
      });
  }

  onSelectedRowsChanged(items: Computer[]) {
    this.selected = items;

    this.emitChanges.emit(this.isSpecificCase ? this.getSpecifiedSelectionList() : this.selected);
  }

  getCompName(computer: Computer): string {
    if (computer.name) return Computer.getComputerName(computer);
    if (computer.computerName) return computer.computerTag ? `${computer.computerTag} [${computer.computerName}]` : computer.computerName;

    return '-';
  }

  public trackBy(item) {
    return item.hid;
  }

  public selectedText(): string {
    return tableSelectedEntitiesText({
      i18nPipe: this.i18next,
      count: this.selected.length,
      isSelectAllOnAllPages: false,
      entityPluralKey: 'shared:computers.computer'
    });
  }

  public selectAllText(): string {
    return tableSelectAllEntitiesText({
      i18nPipe: this.i18next,
      total: this.total,
      entityPluralKey: 'shared:computers.computer'
    });
  }

  public selectAllOnAllPages(should: boolean) {
    if (should && this.total !== this.data.length) {
      const streams$: Observable<any>[] = [];
      const amountOfLoads = Math.round(this.total / this.limit);

      for (let i = 1; i <= amountOfLoads; i++)
        streams$.push(
          this.getLoadStreamWithParams({
            ...this.currentParams,
            orderBy: this.orderBy,
            offset: this.currentParams.offset + i * this.limit
          })
        );

      if (streams$.length) {
        this.loading = true;

        forkJoin(streams$)
          .pipe(debounceTime(300), untilDestroyed(this))
          .subscribe({
            next: () => {
              this.total = this.data.length;
              this.selected = cloneDeep(this.data);
              this.loading = false;
              this.emitChanges.emit(this.isSpecificCase ? this.getSpecifiedSelectionList() : this.selected);
            },
            error: () => {
              this.loading = false;
            }
          });
      }
    }
  }

  ngAfterViewInit(): void {
    this.smartSearch.search
      .pipe(
        untilDestroyed(this),
        filter((v) => !!v),
        withLatestFrom(this.tagFacade.allTags$),
        switchMap(([filter, tags]) =>
          this.tagFacade.isTagsFilterValid(this.smartSearch.value).pipe(map((state) => ({ filter, state, tags })))
        ),
        tap(({ filter, state, tags }) => {
          this.smartSearch.modelValidationState = state;

          this.currentParams = this.getParametersFromSmartFilter(filter, state, tags);

          if (this.computerSearchForm.getRawValue().companies?.length) {
            this.currentParams.companyIds = this.computerSearchForm.getRawValue().companies.map((company) => company.id);
          }

          const os = this.computerSearchForm.get('os').value?.map((os) => os.id);

          if (os) this.currentParams = { ...this.currentParams, os };
        }),
        tap(() => (this.loading = true)),
        switchMap(() => this.getLoadStreamWithParams({ ...this.currentParams, offset: 0, limit: this.limit, orderBy: this.orderBy }))
      )
      .subscribe({
        next: () => (this.loading = false),
        error: () => {
          this.loading = false;
          this.data = [];
        }
      });
  }

  getAdditionalParams() {
    const params: GetComputersParams = {};
    if (!this.showOffline && !this.allFilters) params.online = true;
    if (this.osType && !this.allFilters) params.os = [this.osType];
    if (this.agentType && !this.allFilters) params.appIds = [this.agentType];

    return params;
  }

  getParametersFromSmartFilter(filter: SmartSearchModel, state: SmartSearchValidationState, tags: Tag[]): GetComputersParams {
    const offlineState = 'Offline';
    const params: GetComputersParams = {};

    if (SmartSearchTemplateName.Status in filter) params.online = filter[SmartSearchTemplateName.Status][0].value !== offlineState;

    if (SmartSearchTemplateName.IP in filter) params.ipAddresses = getSmartSearchTagValues(SmartSearchTemplateName.IP, filter);

    if (SmartSearchTemplateName.ProductInstalled in filter)
      params.appIds = getSmartSearchTagValues(SmartSearchTemplateName.ProductInstalled, filter) as AgentType[];

    if (SmartSearchTemplateName.ProductNotInstalled in filter)
      params.appIdsNotInstalled = getSmartSearchTagValues(SmartSearchTemplateName.ProductNotInstalled, filter) as AgentType[];

    if (SmartSearchTemplateName.OS in filter) params.os = getSmartSearchTagValues(SmartSearchTemplateName.OS, filter) as OsType[];

    if (filter?.words?.length && filter.words[0]?.value != '')
      params.search = getSmartSearchTagValues(SmartSearchTemplateName.Words, filter).join(' ');

    const recievedTags = (filter?.Tag as any[])?.flatMap((tag) => tag.value ?? []);

    if (recievedTags?.length) {
      const removedTags = recievedTags.filter((tag) => tags.map((storedTag) => storedTag.name !== tag)).filter((item) => Number(item));
      if (state !== SmartSearchValidationState.Valid && !removedTags.length) return {};

      params.tags = tags
        .filter((tag) => recievedTags.includes(tag.name))
        .map((tag) => tag.id)
        .concat(removedTags);
    }

    return params;
  }

  private getSpecifiedSelectionList(): Partial<Computer>[] {
    return this.data
      .filter((computer) => {
        if (this.selected.some((pickedComputer) => pickedComputer.hid === computer.hid)) {
          return computer.autoUpdateDisabled ? false : true;
        }

        return computer.autoUpdateDisabled ? true : false;
      })
      .map((computer) => ({ hid: computer.hid, autoupdatedisabled: !computer.autoUpdateDisabled }));
  }
}
